/**
 * "Something went wrong" - the offer to send a problem report (2026-10-05).
 *
 * The operator's rules (`.notepads/diagnostics-reports.md`): a report about a
 * crash is sent only when the person says so; they can dismiss it; or they
 * can opt in, once, to automatic sending - ONE suite-wide setting held by
 * OverpoweredApps (`reports_auto_send`), which this reads before sending
 * anything without asking.
 *
 * Never on stage: while the suite is in performance mode nothing is shown; the
 * offer waits until the show is over. Nor before OPButtons KNOWS whether it is
 * (2026-10-06): a freeze is often restarted mid-gig, and an error in the first
 * seconds of that restart, before OPBridge has answered, must not pop up on
 * stage. Only where a show is possible: with no OPBridge paired, "not known"
 * can never become known, and holding would silence every offer for a new user. Never nagging: one offer per NEW
 * distinct error, at most one every 10 minutes and three per run. Noise the
 * browser raises on its own (a ResizeObserver loop, a cancelled request,
 * a cross-origin "Script error.") is recorded but never offered.
 */

import { Show, createSignal, createEffect, on, onCleanup, onMount, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import { onNewError, type CapturedError } from '../diag/errors.ts';
import { performanceMode, performanceModeKnown } from '../opbridge/performanceMode.ts';
import { bridges } from '../opbridge/store.ts';
import { authStatus } from '../auth/opid.ts';
import { readAutoSend, writeAutoSend } from '../diag/helpDesk.ts';
import { buildDiagReport } from '../diag/report.ts';
import { toOpReport } from '../diag/opReport.ts';
import { submitReport } from '../diag/reportQueue.ts';
import { helpOpen, openHelp } from '../help/store.ts';
import { pushToast } from '../ui/toast.ts';

const QUIET_MS = 10 * 60_000;
const MAX_PER_RUN = 3;

/** Errors the browser raises by itself; never worth asking a person about. */
export function isNoise(message: string): boolean {
  return /ResizeObserver loop|AbortError|The operation was aborted|^Script error\.?$|Load failed$|NetworkError when attempting/i.test(message);
}

async function autoSend(): Promise<void> {
  const out = toOpReport(buildDiagReport(), { type: 'bug', note: '', sent: 'auto', diagnostics: true });
  const o = await submitReport(out);
  pushToast('ok', o.kind === 'sent' ? 'A problem report was sent automatically.' : 'A problem report was saved and will send automatically.');
}

export function CrashPrompt(): JSX.Element {
  const [offer, setOffer] = createSignal<CapturedError | null>(null);
  // Performing, or a computer is paired and has not said yet (a mid-gig restart).
  const onStageOrMaybe = () => performanceMode() || (!performanceModeKnown() && bridges().length > 0);
  let waiting: CapturedError | null = null;
  let lastAt = 0;
  let count = 0;

  const consider = async (e: CapturedError) => {
    if (isNoise(e.message) || helpOpen()) return;
    if (onStageOrMaybe()) { waiting = e; return; }
    if (count >= MAX_PER_RUN || Date.now() - lastAt < QUIET_MS) return;
    lastAt = Date.now();
    count += 1;
    if (authStatus() === 'signed-in') {
      const auto = await readAutoSend();
      if (auto.ok && auto.value) { void autoSend(); return; }
    }
    setOffer(e);
  };

  onMount(() => {
    const off = onNewError((e) => void consider(e));
    onCleanup(off);
  });
  createEffect(on(() => !onStageOrMaybe(), (clear) => {
    if (clear && waiting) { const e = waiting; waiting = null; void consider(e); }
  }, { defer: true }));

  const send = () => { setOffer(null); openHelp({ v: 'report', type: 'bug' }); };
  const always = async () => {
    setOffer(null);
    const r = await writeAutoSend(true);
    if (r.ok) { pushToast('ok', 'Problem reports now send automatically, from every OverpoweredApps app.'); void autoSend(); }
    else openHelp({ v: 'report', type: 'bug' });
  };

  return (
    <Show when={offer()}>
      <Portal mount={shellPortal() ?? document.body}>
        <div class="crash-prompt" role="alertdialog" aria-label="Something went wrong" data-testid="crash-prompt">
          <span class="crash-prompt-title">Something went wrong in OPButtons.</span>
          <span class="crash-prompt-body">Send a report so it can be fixed? You see what goes with it before it is sent.</span>
          <span class="sk-prow">
            <button type="button" class="sk-b" data-s="live" onClick={send}>Send report</button>
            <button type="button" class="sk-b" onClick={() => setOffer(null)}>Not now</button>
            <Show when={authStatus() === 'signed-in'}>
              <button type="button" class="sk-b" data-s="ghost" onClick={() => void always()}>Always send (every OverpoweredApps app)</button>
            </Show>
          </span>
        </div>
      </Portal>
    </Show>
  );
}
